/**
 * LECTURA ÚNICA DEL ESTIMADO — tipos del lado del navegador (mig 0498).
 *
 * Espejo de `backend/src/modules/estimateReadings/tipos.ts`. Se copian a mano y no se importan
 * porque el frontend no compila nada de `backend/`; si allá cambia una forma, hay que tocar acá.
 * Dos principios que esta forma hace cumplir (dueño, 2026-10-07): toda cifra importante lleva
 * {value, raw_text, page, confidence} —lo ilegible queda como `unreadable`, no como un número—, y
 * cada partida trae su página para saber seis meses después de dónde salió.
 *
 * Las partidas usan claves CORTAS (n, desc, qty, up…) a propósito: un estimado tiene 100-400 y
 * cada carácter de clave se repite por partida en la salida del modelo.
 */
export type Confianza = 'high' | 'medium' | 'low' | 'unreadable';

/** Una cifra con su evidencia. */
export type Cifra = { value: number | null; raw_text: string | null; page: number | null; confidence: Confianza };
/** Un texto con su evidencia (claim number, fecha, nombre). */
export type Texto = { value: string | null; raw_text: string | null; page: number | null; confidence: Confianza };

export type Partida = {
  n: number | null;
  structure: string | null;
  area: string | null;
  cat: string | null;
  sel: string | null;
  act: string | null;
  desc: string;
  qty: number | null;
  unit: string | null;
  up: number | null;
  tax: number | null;
  op: number | null;
  rcv: number | null;
  dep_pct: number | null;
  dep: number | null;
  acv: number | null;
  age_life: string | null;
  page: number | null;
  conf: 'high' | 'medium' | 'low';
  /** Línea impresa TACHADA (ítem quitado): no entra en los totales (lector v3). */
  struck?: boolean;
  /** Columnas REMOVE / REPLACE (nuestros estimados): las dos partes de `up`. */
  up_rm?: number | null;
  up_rp?: number | null;
  /** La agregó una persona desde Resolver (mig 0510), no el lector. */
  manual?: boolean;
};

export type LecturaEstimado = {
  document: {
    platform: string | null;
    estimate_type: string | null;
    estimate_number: string | null;
    estimate_name: string | null;
    estimate_date: Texto;
    date_created: string | null;
    date_revised: string | null;
    pages_total: number | null;
    prepared_by: { company: string | null; person: string | null; role: 'carrier' | 'public_adjuster' | 'contractor' | 'unknown' };
    side: 'ours' | 'insurance' | 'unknown';
    adjuster: { name: string | null; email: string | null; phone: string | null };
    estimator: string | null;
  };
  claim: {
    insured_name: Texto;
    co_insured: string | null;
    property_address: Texto;
    city: string | null;
    state: string | null;
    zip: string | null;
    claim_number: Texto;
    policy_number: Texto;
    date_of_loss: Texto;
    cause_of_loss: string | null;
    inspection_date: string | null;
  };
  pricing: {
    price_list_code: Texto;
    price_list_date: string | null;
    price_list_location: string | null;
    zip: string | null;
  };
  policy: {
    settlement_basis: 'RCV' | 'ACV' | null;
    coverages: Array<{ name: string; limit: Cifra }>;
    deductibles: Array<{ type: string | null; amount: Cifra; percent: number | null; coverage: string | null; applied_in_calculation: boolean | null }>;
    notes: string | null;
  };
  financial_summary: {
    rcv: Cifra;
    acv: Cifra;
    depreciation: Cifra;
    recoverable_depreciation: Cifra;
    nonrecoverable_depreciation: Cifra;
    deductible: Cifra;
    prior_payments: Cifra;
    net_claim: Cifra;
    overhead_profit: Cifra;
    sales_tax: Cifra;
    line_item_total: Cifra;
    grand_total: Cifra;
    /** Ajuste por Base Service Charges (price lists RSR); ausente en lecturas v2. */
    base_service_charges?: Cifra;
    /** Retenido hasta incurrir (code upgrades / Ordinance or Law): en el RCV y no en el ACV. */
    paid_when_incurred?: Cifra;
    by_coverage: Array<{ coverage: string; rcv: number | null; acv: number | null; depreciation: number | null; deductible: number | null; net_claim: number | null; page: number | null; manual?: boolean }>;
    by_structure: Array<{ structure: string; rcv: number | null; page: number | null }>;
  };
  structures: Array<{ name: string; coverage: string | null; areas: Array<{ name: string; path: string | null; line_count: number | null; rcv_subtotal: number | null; page: number | null }> }>;
  line_items: Partida[];
  flags: {
    roof_full_replacement: boolean | null;
    has_building_code_items: boolean | null;
    code_upgrade_items: Array<{ desc: string; amount: number | null; page: number | null }>;
  };
  uncertain_fields: Array<{ field: string; page: number | null; possible_value: string | null; reason: string }>;
  warnings: string[];
};

/** Un chequeo aritmético hecho en código (no por el modelo). */
export type Chequeo = {
  check: string;
  source_value: number | null;
  calculated_value: number | null;
  delta: number | null;
  status: 'ok' | 'mismatch' | 'skipped';
  severity: 'high' | 'low';
  note: string | null;
};

export type Semaforo = 'green' | 'yellow' | 'red';

export type ValidacionLectura = {
  checks: Chequeo[];
  line_mismatches: Array<{ n: number | null; desc: string; page: number | null; printed: number; calculated: number }>;
  key_fields_low_confidence: string[];
  semaforo: Semaforo;
  reasons: string[];
  sums: { lines_rcv: number | null; lines_count: number; struck_count?: number; by_structure: Record<string, number> };
  /** Alguien aceptó la lectura tal cual (Resolver): el semáforo quedó verde y acá dice quién, cuándo y por qué. */
  accepted?: { by_name: string; at: string; note: string; semaforo_before: Semaforo };
  resolved_count?: number;
};

/** Los totales del resumen que se pueden corregir a mano (mismo orden que el servidor). */
export const CAMPOS_CIFRA = ['rcv', 'acv', 'depreciation', 'recoverable_depreciation', 'nonrecoverable_depreciation', 'deductible', 'prior_payments', 'net_claim', 'overhead_profit', 'sales_tax', 'line_item_total', 'grand_total', 'base_service_charges', 'paid_when_incurred'] as const;
export type CampoCifra = (typeof CAMPOS_CIFRA)[number];

/** Una resolución a mano sobre la lectura (mig 0510), como la guarda el servidor. */
export type Resolucion = (
  | { kind: 'add_block'; label: string; amount: number; paid_when_incurred: boolean; acv: number | null; depreciation: number | null; page: number | null }
  | { kind: 'add_line'; desc: string; structure: string | null; area: string | null; cat: string | null; sel: string | null; act: string | null; qty: number | null; unit: string | null; up: number | null; tax: number | null; op: number | null; rcv: number; dep: number | null; acv: number | null; page: number | null; add_to_totals: boolean }
  | { kind: 'set_figure'; field: CampoCifra; value: number | null; page: number | null }
  | { kind: 'accept' }
) & { id: string; note: string; by_user_id: string | null; by_name: string; at: string };

/** El estado por archivo que viaja en la lista de Files y en el polling. */
export type ResumenLectura = {
  file_id: string;
  status: 'queued' | 'reading' | 'done' | 'failed';
  semaforo: Semaforo | null;
  line_count: number | null;
  uncertain_count: number | null;
  warning_count: number | null;
  model: string | null;
  via: string | null;
  latency_ms: number | null;
  tokens_in: number | null;
  tokens_out: number | null;
  read_at: string | null;
  started_at: string | null;
  error: string | null;
  copied: boolean;
  reader_version: number;
  side: 'ours' | 'insurance' | 'unknown' | null;
  rcv: number | null;
  estimate_date: string | null;
  /** Resoluciones a mano aplicadas (0 = tal cual la dejó el lector). */
  resolved_count?: number;
};

/** Lo que devuelve GET /api/files/:id/reading (y lo que devuelven las rutas de Resolver, ya recalculado). */
export type LecturaCompleta = { meta: ResumenLectura; reading: LecturaEstimado; validation: ValidacionLectura; resolutions?: Resolucion[] };

/** ¿Está en cola o leyéndose? (el chip gira y hay que seguir preguntando). */
export function lecturaEnCurso(r: ResumenLectura | null | undefined): boolean {
  return r?.status === 'queued' || r?.status === 'reading';
}

/**
 * Dinero en DÓLARES (la lectura viene en dólares, no en centavos como el resto del app, porque es
 * lo que está impreso en el papel). null → "—": una celda vacía dice "no estaba", un 0 mentiría.
 * Siempre en-US: el separador de miles no cambia por idioma en una empresa de EE. UU.
 */
export function fmtMoney(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  const abs = Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${v < 0 ? '-' : ''}$${abs}`;
}

/** Cantidad sin signo de dólar (qty, porcentajes): hasta 2 decimales, sin ceros de relleno. */
export function fmtNum(v: number | null | undefined, maxDecimals = 2): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return v.toLocaleString('en-US', { maximumFractionDigits: maxDecimals });
}

/** Milisegundos → "38s" o "2m 05s": lo que el dueño quiere mirar para saber "qué tan rápido". */
export function fmtSegundos(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
}

/** El color del semáforo como variable del tema (amarillo = el naranja del sistema). */
export function colorSemaforo(s: Semaforo | null | undefined): string {
  return s === 'green' ? 'var(--green)' : s === 'yellow' ? 'var(--orange)' : s === 'red' ? 'var(--red)' : 'var(--muted)';
}

/** El color de una confianza: alta no se pinta, media azul, baja naranja, ilegible rojo. */
export function colorConfianza(c: Confianza | 'high' | 'medium' | 'low' | null | undefined): string {
  return c === 'low' ? 'var(--orange)' : c === 'unreadable' ? 'var(--red)' : c === 'medium' ? 'var(--accent)' : 'var(--green)';
}

/* ───────────── Desglose en código (GET /api/files/:id/reading/breakdown) ───────────── */
export type GrupoDesglose = { key: string; label: string; lines: number; base: number; tax: number; op: number; rcv: number };
export type Desglose = {
  options: { sinOp?: boolean; sinTax?: boolean };
  mode: 'per_line_extras' | 'extras_in_totals';
  totals: { lines: number; base: number; tax: number; op: number; bsc: number; rcv: number; rcv_full: number; rcv_printed: number | null };
  rates: { tax_pct: number | null; op_pct: number | null };
  by_category: GrupoDesglose[]; by_zone: GrupoDesglose[]; by_activity: GrupoDesglose[];
  notes: string[];
};
